import { isEmpty } from '@jjj/components';
import actions from './actions';
import { createReducer, getError } from '../../../../helpers/reducer';
import { keyBy } from '../../../../helpers/core';
import { setDefaultCountryField, isPostalCode } from '../../../../helpers/countriesHelpers';
import { isBench, isMaof } from '../../../../settings';
import { t } from '../../../../i18n';

const initState = {
  baseError: null,
  client: null,
  deleted: false,
  editForm: null,
  filteredSubUnits: [],
  formInfo: { countries: [] },
  isEdit: false,
  loading: false,
};

const initialClientData = {
  account_manager: null,
  account_size: 'unknown',
  activation_rep: null,
  active: 'false',
  address: '',
  backups_enabled: true,
  breaks_compensation: false,
  breaks_duration: 0,
  breaks_enabled: false,
  city: '',
  clocking_type: 'geoposition',
  company: '',
  company_image_url: '',
  company_logo_url: '',
  cost_center: null,
  country: '',
  division: '',
  industry: '',
  industry_id: '',
  legal_entity_id: '',
  name: '',
  past_jobs_count: '',
  rating: '',
  sales_rep: null,
  sector: '',
  sector_id: '',
  shifts_auto_approve_enabled: false,
  state: '',
  street: '',
  subunits: [],
  zip: '',
  booked_tier_pay_rate_enabled: null,
};

function onLoad(state) {
  return {
    ...state,
    loading: true,
    isEdit: false,
  };
}

function onLoadSuccess(state, { payload }) {
  const { data } = payload;
  const { ...restProps } = data;

  const client = {
    ...restProps,
    address: '',
    industry_id: data.industry ? data.industry.id : '',
    sector_id: data.sector ? data.sector.id : '',
  };

  return {
    ...state,
    client,
    loading: false,
  };
}

function onClearStore() {
  return { ...initState };
}

function validate(attr, value) {
  let error = '';

  if (attr === 'name') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.units.unit.name_label') });
    }
  }

  if (attr === 'cost_center' && isBench()) {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.units.unit.cost_center_label') });
    }
  }

  if (attr === 'country') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.units.unit.country_label') });
    }
  }

  if (attr === 'street') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.units.unit.street_label') });
    }
  }

  if (attr === 'city') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.units.unit.city_label') });
    }
  }

  if (attr === 'state' && !isMaof()) {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.units.unit.state_label') });
    }
  }

  if (attr === 'zip' && !isMaof()) {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.common.location.postal_code') });
    } else if (!isPostalCode(value)) {
      error = t('dash.validation.valid_error', {
        fieldName: t('dash.common.location.postal_code'),
      });
    }
  }

  if (attr === 'sector_id') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.units.unit.sector_label') });
    }
  }

  if (attr === 'country') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.units.unit.country_label') });
    }
  }

  return error;
}

function onEdit(state) {
  const { client } = state;

  const convertedClient = {
    ...client,
    ...{ company: '', division: '' },
    // An absent field must stay distinct from "off" - materialising it would send `false` on save.
    ...(client.dynamic_banding_enabled !== undefined && {
      dynamic_banding_enabled: Boolean(client.dynamic_banding_enabled),
    }),
    ...(client.parent && client.parent.level === 'company' && { company: client.parent.id, division: '' }),
    ...(client.parent && client.parent.level === 'division' && { division: client.parent.id, company: '' }),
  };

  const propsArray = Object.keys(convertedClient);

  const clientProp = prop =>
    prop === 'subunits'
      ? convertedClient[prop].map(item => {
          return { value: item.id, label: item.name };
        })
      : convertedClient[prop];

  const getFormValue = prop => {
    if (prop === 'state') {
      return convertedClient[prop]?.id;
    }

    return convertedClient[prop] && Object.prototype.hasOwnProperty.call(convertedClient[prop], 'id')
      ? convertedClient[prop].id
      : clientProp(prop);
  };

  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: getFormValue(prop),
      error: prop === 'breaks_duration' && !convertedClient.breaks_enabled ? '' : validate(prop, convertedClient[prop]),
      touched: false,
    }),
  );

  if (editForm.active) {
    editForm.active.value = editForm.active.value ? 'true' : 'false';
  }

  return {
    ...state,
    isEdit: true,
    editForm,
  };
}

function onSaveSuccess(state) {
  return {
    ...state,
    client: null,
    isEdit: false,
    editForm: null,
  };
}

function onSaveError(state, { payload }) {
  const { data } = payload;

  const propsArray = Object.entries(state.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        error: getError(entry[0], data),
        touched: true,
      };
    },
  );

  const baseError = data.base ? data.base[0] : null;

  return {
    ...state,
    editForm,
    baseError,
  };
}

function onDeleteSuccess(state) {
  return {
    ...state,
    deleted: true,
  };
}

function onCreate(state) {
  const propsArray = Object.keys(initialClientData);

  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: initialClientData[prop],
      error: validate(prop, initialClientData[prop]),
      touched: false,
    }),
  );

  return {
    ...state,
    isEdit: true,
    client: initialClientData,
    editForm: {
      ...editForm,
      country: setDefaultCountryField(),
      active: {
        ...editForm.active,
        value: state.formInfo.activation_statuses[0].id,
      },
      booked_tier_pay_rate_enabled: {
        ...editForm.booked_tier_pay_rate_enabled,
        value: state?.formInfo?.default_booked_tier_pay_rate_enabled || false,
      },
    },
  };
}

function onChangeProperty(state, { payload }) {
  const { attr, value } = payload;
  const { editForm } = state;

  editForm[attr] = {
    value,
    error: validate(attr, value),
    name: attr,
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeProperties(state, { payload }) {
  const { attributes } = payload;
  const propsArray = Object.keys(attributes);

  const editFormProps = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: attributes[prop],
      error: validate(prop, attributes[prop]),
      touched: true,
    }),
  );

  const editForm = {
    ...state.editForm,
    ...editFormProps,
  };

  return {
    ...state,
    editForm,
  };
}

function onCancel(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onLoadFormDataSuccess(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    formInfo: {
      ...state.formInfo,
      ...data,
    },
  };
}

function onLoadManagersFormData(state, { payload }) {
  const { data } = payload;

  const administrators = data.map(admin => {
    return {
      id: admin.id,
      name: `${admin.first_name} ${admin.last_name}`,
    };
  });

  return {
    ...state,
    formInfo: {
      ...state.formInfo,
      administrators,
    },
  };
}

function onFilterSubUnitsSuccess(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    filteredSubUnits: data,
  };
}

function onChangeSelectedSubUnits(state, { payload }) {
  const { subunits } = payload;
  const editForm = { ...state.editForm };

  editForm.subunits = {
    value: subunits,
    error: '',
    name: 'subunits',
    touched: true,
  };

  return {
    ...state,
    editForm,
    baseError: null,
  };
}

export { validate };
export default createReducer(initState, {
  [actions.LOAD]: onLoad,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.EDIT]: onEdit,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CHANGE_PROPERTIES]: onChangeProperties,
  [actions.CANCEL]: onCancel,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.SAVE_ERROR]: onSaveError,
  [actions.DELETE_SUCCESS]: onDeleteSuccess,
  [actions.CREATE]: onCreate,
  [actions.TOUCH]: onTouch,
  [actions.LOAD_FORM_DATA_SUCCESS]: onLoadFormDataSuccess,
  [actions.LOAD_MANAGERS_FORM_DATA]: onLoadManagersFormData,
  [actions.FILTER_SUB_UNITS_SUCCESS]: onFilterSubUnitsSuccess,
  [actions.CHANGE_SELECTED_SUB_UNITS]: onChangeSelectedSubUnits,
});
